import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';

import { semanticColor } from '../../theme/semanticColor';
import { Icon, IconType } from '../icon/Icon';

import { BaseButton, BaseButtonProps } from './BaseButton';

export const iconButtonVariants = cva('inline-flex items-center justify-center whitespace-nowrap transition-colors disabled:pointer-events-none rounded-full', {
  variants: {
    variant: {
      main: 'hover:bg-neutral-background-hovered&pressed',
      'main-foreground': 'hover:bg-neutral-background-hovered&pressed',
      inverse: 'hover:bg-neutral-background-inverse-hovered&pressed ',
      dimmed:
        'bg-neutral-background-dimmed-low  hover:bg-gradient-to-r from-neutral-background-hovered&pressed to-neutral-background-hovered&pressed disabled:bg-neutral-background-disabled-main',
      'filled-secondary':
        'bg-secondary-background-main hover:bg-gradient-to-r from-neutral-background-inverse-hovered&pressed to-secondary-background-main disabled:bg-neutral-background-disabled-main',
      'filled-primary':
        'bg-primary-background-main hover:bg-gradient-to-r from-neutral-background-hovered&pressed to-primary-background-hovered&pressed disabled:bg-neutral-background-disabled-main'
    },
    size: {
      small: 'w-[38px] h-[38px]',
      xsmall: 'w-[30px] h-[30px]'
    }
  },
  defaultVariants: {
    variant: 'main',
    size: 'small'
  }
});

const ICON_SIZE = { small: 24, xsmall: 20 };

const LOADING_COLOR = {
  main: semanticColor.neutral.foreground.disabled,
  'main-foreground': semanticColor.neutral.foreground.disabled,
  inverse: semanticColor.neutral.foreground['inverse-disabled'],
  dimmed: semanticColor.neutral.foreground.disabled
};

const ICON_COLOR = {
  main: semanticColor.neutral.foreground.low,
  'main-foreground': semanticColor.neutral.foreground.main,
  inverse: semanticColor.neutral.foreground['inverse-main'],
  dimmed: semanticColor.secondary.foreground['inverse-main'],
  'filled-primary': semanticColor.secondary.foreground['inverse-main'],
  'filled-secondary': semanticColor.secondary.foreground['inverse-main']
};

export interface IconButtonProps extends Omit<BaseButtonProps, 'loadingColor'>, VariantProps<typeof iconButtonVariants> {
  iconType: IconType;
  serviceName?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant = 'main', size, iconType, disabled, serviceName, ...props }, ref) => {
    return (
      <BaseButton
        className={iconButtonVariants({ variant, size, className })}
        ref={ref}
        loadingColor={LOADING_COLOR[variant as keyof typeof LOADING_COLOR]}
        disabled={disabled}
        {...props}
      >
        <Icon
          type={iconType}
          size={ICON_SIZE[size || 'small']}
          color={disabled ? semanticColor.neutral.foreground.disabled : ICON_COLOR[variant as keyof typeof ICON_COLOR]}
          serviceName={serviceName}
        />
      </BaseButton>
    );
  }
);

IconButton.displayName = 'BaseButton';
